import { markListStructure } from "authoring/routes/project/schedule-editor/lib/mark-list-structure.mjs"
import { markFormattingStructure } from "authoring/routes/project/schedule-editor/lib/mark-formatting-structure.mjs"

const TEXT_NODE = 3
const COMMENT_NODE = 8
const ELEMENT_NODE = 1

/**
 * @param {() => HTMLElement | null} getContainer
 * @returns {{
 *   stripAuthoringComparisonArtifacts: (value?: string) => string,
 *   normalizeAuthoringComparisonText: (value?: string) => string,
 *   normalizeAuthoringPlainText: (value?: string) => string,
 *   normalizeAuthoringSnapshotHtml: (value?: string) => string,
 *   normalizeAuthoringComparisonDiffHtml: (value?: string) => string,
 *   htmlToText: (html: string, lineBreak?: boolean) => string,
 * }}
 */
export function createAuthoringComparisonNormalizers(getContainer) {
    return {
        stripAuthoringComparisonArtifacts,
        normalizeAuthoringComparisonText,
        normalizeAuthoringPlainText,
        normalizeAuthoringSnapshotHtml,
        normalizeAuthoringComparisonDiffHtml,
        htmlToText,
    }

    function stripAuthoringComparisonArtifacts(value = "") {
        if (!value || value === "undefined") return ""

        return `${value}`
            .replace(/<!--[\s\S]*?-->/g, "")
            .replace(/<span[^>]*mso-element:field-(?:begin|end)[^>]*>[\s\S]*?<\/span>/gi, "")
            .replace(/<span[^>]*>\s*xe\s*(?:&quot;|").*?(?:&quot;|")\s*<\/span>/gi, "")
            .replace(/xe\s*(?:&quot;|").*?(?:&quot;|")/gi, "")
            .replace(/\{?\s*if\s+supportFields\s*\}?/gi, "")
            .replace(/supportFields/gi, "")
    }

    function htmlToText(html, lineBreak = false) {
        if (!html || html === "undefined") return ""

        const container = getContainer()
        if (!container) {
            return `${html}`
                .replace(/<[^>]+>/g, " ")
                .replace(/\s+/g, " ")
                .trim()
        }

        container.innerHTML = html
        container.querySelectorAll("p, div, br").forEach((el) => el.insertAdjacentText("afterend", "\n"))

        // Containers are detached; use DOM text rather than implementation-specific layout text.
        const innerText = container.textContent ?? ""
        let result = lineBreak ? innerText : innerText.replace(/\n+/g, " | ").trim()
        if (result.endsWith("|")) {
            result = result.slice(0, -2)
        }
        return result
    }

    function normalizeAuthoringComparisonText(value = "") {
        const html = cleanHtml(value)
        const withFormatting = markFormattingStructure(html)
        const withLists = markListStructure(withFormatting)
        return toComparableText(withLists)
    }

    // For checks that care only about the words, such as whether a recommendation has been applied.
    // Recommendation text is stored as plain words, so list markers would stop it matching list content.
    function normalizeAuthoringPlainText(value = "") {
        const html = cleanHtml(value)
        return toComparableText(html)
    }

    function cleanHtml(value) {
        return stripAuthoringComparisonArtifacts(value).replace(/\n/gi, "").replace(/\r/gi, "")
    }

    function toComparableText(html) {
        const text = htmlToText(html)
        return text
            .trim()
            .replace(/\s*\|\s*/g, " ")
            .replace(/([.,!?;:])\s+/g, "$1")
            .replace(/&\w+;/gi, "")
            .replace(/\s+/g, " ")
            .toLowerCase()
    }

    function normalizeAuthoringSnapshotHtml(value = "") {
        if (!hasNestedListStructure(value)) {
            return normalizeAuthoringComparisonText(value)
        }

        return normalizeAuthoringStructuralHtml(value)
    }

    function normalizeAuthoringStructuralHtml(value = "") {
        if (!value || value === "undefined") return ""

        const cleaned = cleanHtml(value)
        const container = getContainer()
        if (!container) {
            return cleaned.replace(/>\s+</g, "><").trim()
        }

        container.innerHTML = cleaned
        normalizeStructuralNode(container)

        return container.innerHTML.replace(/>\s+</g, "><").trim()
    }

    function normalizeAuthoringComparisonDiffHtml(value = "") {
        if (!value || value === "undefined") return value

        const html = cleanHtml(value)
        const text = htmlToText(html, true)
        return text
            .trim()
            .replace(/\n+/g, " ")
            .replace(/([.,!?;:])\s+/g, "$1 ")
            .replace(/&\w+;/gi, "")
            .replace(/\s+/g, " ")
            .replaceAll("...", "\u2026")
    }

    function hasNestedListStructure(value = "") {
        if (!value) return false

        const container = getContainer()
        if (!container) return false

        container.innerHTML = `${value}`
        return Boolean(container.querySelector("ol ol, ol ul, ul ol, ul ul"))
    }

    function normalizeStructuralNode(parent) {
        for (const node of [...parent.childNodes]) {
            if (node.nodeType === COMMENT_NODE) {
                node.remove()
                continue
            }

            if (node.nodeType === TEXT_NODE) {
                normalizeStructuralTextNode(node)
                continue
            }

            if (node.nodeType === ELEMENT_NODE) {
                normalizeStructuralNode(node)
            }
        }
    }

    function normalizeStructuralTextNode(node) {
        const nextValue = node.textContent.replace(/\u00a0/g, " ").replace(/\s+/g, " ")
        if (!nextValue.trim()) {
            node.remove()
            return
        }

        node.textContent = nextValue
    }
}
